<template>
    <div
        class="categories-menu scrollbar"
        style="height: 100vh; padding-bottom: 300px; overflow-y: auto;"
    >
        <v-skeleton-loader
            v-show="loading"
            type="list-item-two-line@6"
        ></v-skeleton-loader>

                <!-- 分类速搜过滤栏 -->
        <div class="category-quick-filter" v-if="!loading && navInfos && navInfos.length > 0">
            <div class="filter-input-wrap">
                <v-icon small color="#0d9488" class="mr-1">mdi-magnify</v-icon>
                <input
                    type="text"
                    v-model.trim="categorySearchQuery"
                    placeholder="速搜分类..."
                    class="quick-filter-input"
                />
                <button
                    v-if="categorySearchQuery"
                    class="clear-filter-btn"
                    @click="categorySearchQuery = ''"
                    title="清空"
                >
                    <v-icon x-small color="#94a3b8">mdi-close-circle</v-icon>
                </button>
            </div>
        </div>

        <v-list v-show="!loading" nav dense>
            <v-list-group
                v-for="mainCategory in filteredNavInfos"
                :key="mainCategory.driveId"
                :value="isMainCategoryExpanded(mainCategory)"
                @input="onToggleMainCategory(mainCategory, $event)"
            >
                <!-- 一级分类标题 -->
                <template v-slot:activator>
                    <v-list-item-icon class="main-cat-icon">
                        <div
                            class="folder-badge"
                            :class="{
                                'is-active':
                                    isMainCategoryExpanded(mainCategory) ||
                                    isActiveMainCategory(mainCategory)
                            }"
                        >
                            <v-icon size="17">{{
                                isMainCategoryExpanded(mainCategory)
                                    ? 'mdi-folder-open'
                                    : 'mdi-folder'
                            }}</v-icon>
                        </div>
                    </v-list-item-icon>
                    <v-list-item-content class="main-cat-content">
                        <v-list-item-title class="main-cat-title-row">
                            <span
                                class="main-category-title"
                                @mouseenter="onHoverMainCategory(mainCategory)"
                                @mouseleave="onCancelHover"
                                @click="
                                    onClickMainCategoryFromTitle(
                                        mainCategory,
                                        $event
                                    )
                                "
                            >
                                {{ mainCategory.name }}
                            </span>
                            <span
                                v-if="mainCategory.lv1_categories && mainCategory.lv1_categories.length"
                                class="main-cat-count"
                            >
                                {{ mainCategory.lv1_categories.length }}
                            </span>
                        </v-list-item-title>
                    </v-list-item-content>
                </template>

                <!-- 二级分类列表 -->
                <v-list-item
                    v-for="subCategory in mainCategory.lv1_categories"
                    :key="subCategory.fileId"
                    class="sub-cat-item"
                    :class="{
                        'v-list-item--active': isActiveSubCategory(subCategory)
                    }"
                    :input-value="isActiveSubCategory(subCategory)"
                    link
                    @mouseenter="onHoverSubCategory(subCategory)"
                    @mouseleave="onCancelHover"
                    @click="onClickSubCategory(subCategory)"
                >
                    <span class="sub-cat-dot" aria-hidden="true"></span>
                    <v-list-item-content class="sub-cat-content">
                        <v-list-item-title>
                            <span>{{ subCategory.name }}</span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list-group>
        </v-list>
    </div>
</template>

<script>
import { get_api_data } from "@/utils/ajax";
import { mapMutations, mapState } from "vuex";
import categoryCache from "@/utils/categoryCache";

const MENU_CACHE_KEY = "material_libraries_menu_cache_v4";

// 严格校验菜单完整性：必须具备一级库和真实的二级子分类
function isValidMenuData(data) {
    return Array.isArray(data) && data.length > 0 && data.some(item => item.lv1_categories && item.lv1_categories.length > 0);
}

export default {
    name: "CategoriesMenu",
    data() {
        return {
            loading: false,
            expandedCategories: [], // 存储展开的分类
            hoverTimer: null,
            categorySearchQuery: ""
        };
    },
    computed: {
        ...mapState("global", ["navInfos", "churchLifeModel"]),
        filteredNavInfos() {
            if (!this.categorySearchQuery) {
                return this.navInfos || [];
            }
            const q = this.categorySearchQuery.toLowerCase();
            return (this.navInfos || []).map(mainCat => {
                const mainMatch = (mainCat.name || "").toLowerCase().includes(q);
                const matchedSubs = (mainCat.lv1_categories || []).filter(sub => 
                    (sub.name || "").toLowerCase().includes(q)
                );
                if (mainMatch || matchedSubs.length > 0) {
                    return {
                        ...mainCat,
                        lv1_categories: mainMatch ? mainCat.lv1_categories : matchedSubs
                    };
                }
                return null;
            }).filter(Boolean);
        },
    },
    created() {
        // 先检查当前 Vuex 中是否有完整子分类数据
        if (isValidMenuData(this.navInfos)) {
            this.autoExpandActiveCategory();
            this.loading = false;
        } else {
            // 尝试读取本地持久缓存秒开
            try {
                const cached = localStorage.getItem(MENU_CACHE_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (isValidMenuData(parsed)) {
                        this.updateNavInfos(parsed);
                        this.autoExpandActiveCategory();
                        this.loading = false;
                    } else {
                        localStorage.removeItem(MENU_CACHE_KEY);
                    }
                }
            } catch (e) {
                console.warn("读取本地菜单缓存失败:", e);
            }
        }

        // 立即向后端拉取最新完整数据（无延迟防抖）
        this.loadNavData();
    },
    methods: {
        ...mapMutations("global", [
            "updateIsListLoading",
            "updateNavInfos",
            "updateDriveIdsID",
            "updateAllCardList",
            "updateAllCardListMeta",
            "updateLv1_ID"
        ]),
        
        // 智能悬停预取（120ms 防抖，防误触，为秒开就绪）
        onHoverMainCategory(mainCategory) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => {
                if (mainCategory && mainCategory.driveId) {
                    categoryCache.prefetch({
                        driveIds: [mainCategory.driveId],
                        lv1_categories: [],
                        scenes: this.churchLifeModel || 0
                    });
                }
            }, 120);
        },

        onHoverSubCategory(subCategory) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => {
                if (subCategory && subCategory.driveId && subCategory.fileId) {
                    categoryCache.prefetch({
                        driveIds: [subCategory.driveId],
                        lv1_categories: [subCategory.fileId],
                        scenes: this.churchLifeModel || 0
                    });
                }
            }, 120);
        },

        onCancelHover() {
            clearTimeout(this.hoverTimer);
        },

        async loadNavData(force = false) {
            const hasValidData = isValidMenuData(this.navInfos);
            if (!hasValidData || force) {
                this.loading = true;
            }

            try {
                const response = await get_api_data("GET", "libraries-menu");
                if (response && response.data && Array.isArray(response.data)) {
                    const navData = response.data.map(item => ({
                        ...item,
                        lv1_categories: (item.lv1_categories || []).map(lv1Item => ({
                            ...lv1Item,
                            name: lv1Item.name,
                            driveId: lv1Item.driveId || item.driveId
                        }))
                    }));

                    if (isValidMenuData(navData)) {
                        this.updateNavInfos(navData);
                        this.autoExpandActiveCategory();

                        try {
                            localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(navData));
                        } catch (e) {}
                    }
                }
            } catch (error) {
                console.error("加载导航数据失败:", error);
            } finally {
                this.loading = false;
            }
        },

        // 自动展开当前激活分类，或默认展开第一个大类展示二级分类
        autoExpandActiveCategory() {
            const currentCategory = this.$route.params.categories;
            if (
                currentCategory &&
                !this.expandedCategories.includes(currentCategory)
            ) {
                this.expandedCategories.push(currentCategory);
            } else if (
                this.expandedCategories.length === 0 &&
                this.navInfos &&
                this.navInfos.length > 0
            ) {
                // 默认展开首个大类，保证用户第一眼即可清晰查看子菜单
                this.expandedCategories.push(this.navInfos[0].driveId);
            }
        },

        onClickMainCategoryFromTitle(mainCategory, event) {
            event.stopPropagation();
            event.preventDefault();

            // 触发路由跳转到共享云端全量素材页面（展示该共享云端的所有素材）
            if (this.$route.params.categories !== mainCategory.driveId || this.$route.params.materialList) {
                this.GLOBAL.scrollPage = 1;
                try {
                    window.scrollTo({ top: 0, behavior: "instant" });
                } catch (e) {
                    window.scrollTo(0, 0);
                }
                this.$router.push(`/${mainCategory.driveId}`);
                this.updateDriveIdsID([mainCategory.driveId]);
                this.updateLv1_ID([]);
            }

            // 切换展开/折叠状态
            this.toggleMainCategory(mainCategory);

            return false;
        },

        onClickSubCategory(subCategory) {
            console.log("点击二级分类:", subCategory);

            // 跳转到素材列表页面
            if (subCategory.fileId !== this.$route.params.materialList) {
                this.$router.push(
                    `/${subCategory.driveId}/${subCategory.fileId}`
                );
            } else {
                // 仅当用户重复点击当前同一分类时才通过事件触发刷新，消除并发重复请求
                if (this.$P) {
                    this.$P.$emit("clickCategoryUpdateImgList");
                }
            }

            this.GLOBAL.scrollPage = 1;
            try {
                window.scrollTo({ top: 0, behavior: "instant" });
            } catch (e) {
                window.scrollTo(0, 0);
            }
            this.updateDriveIdsID([subCategory.driveId]);
            this.updateLv1_ID([subCategory.fileId]);
        },

        // 判断是否为当前激活的一级分类
        isActiveMainCategory(mainCategory) {
            return mainCategory.driveId === this.$route.params.categories;
        },

        // 判断是否为当前激活的二级分类
        isActiveSubCategory(subCategory) {
            return subCategory.fileId === this.$route.params.materialList;
        },

        // 判断一级分类是否应该展开
        isMainCategoryExpanded(mainCategory) {
            return this.expandedCategories.includes(mainCategory.driveId);
        },

        // 切换一级分类展开/折叠状态
        toggleMainCategory(mainCategory) {
            const index = this.expandedCategories.indexOf(mainCategory.driveId);
            if (index === -1) {
                this.expandedCategories.push(mainCategory.driveId);
            } else {
                this.expandedCategories.splice(index, 1);
            }
        },

        // 处理 v-list-group 的展开/折叠事件
        onToggleMainCategory(mainCategory, isExpanded) {
            const index = this.expandedCategories.indexOf(mainCategory.driveId);
            if (isExpanded && index === -1) {
                this.expandedCategories.push(mainCategory.driveId);
            } else if (!isExpanded && index !== -1) {
                this.expandedCategories.splice(index, 1);
            }
        },

        // 公开方法：刷新数据
        refresh() {
            this.updateNavInfos([]); // 清空缓存
            this.loadNavData();
        },

        // 公开方法：展开所有分类
        expandAll() {
            this.expandedCategories = this.navInfos.map(item => item.driveId);
        },

        // 公开方法：折叠所有分类
        collapseAll() {
            this.expandedCategories = [];
        }
    },

    watch: {
        // 监听路由变化，仅在 categories 参数变化时更新
        '$route.params.categories': function (newCategory) {
            if (newCategory) {
                this.autoExpandActiveCategory();
            }
        }
    }
};
</script>

<style scoped lang="scss">
/* 左侧分类树导航 — 舒展大气、字号饱满的专业树形设计 */
.categories-menu {
    padding: 12px 10px 260px !important;
    user-select: none;

    ::v-deep .v-list {
        background: transparent !important;
        padding: 2px;
    }

    ::v-deep .v-list-group {
        margin-bottom: 6px;

        /* 一级分类头部：饱满大气的 42px 高度 */
        .v-list-group__header {
            min-height: 42px !important;
            height: 42px;
            padding: 0 10px 0 12px !important;
            border-radius: 10px !important;
            transition: background-color 0.18s ease, color 0.18s ease;

            &::before {
                border-radius: 10px;
            }

            &:hover {
                background-color: #ebf8f5 !important;

                .folder-badge:not(.is-active) {
                    background: #daf2eb;
                    border-color: rgba(16, 185, 129, 0.38);
                    transform: scale(1.06);
                    box-shadow: 0 3px 8px -1px rgba(13, 148, 136, 0.22);
                }

                .folder-badge.is-active {
                    transform: scale(1.06);
                    box-shadow: 0 5px 14px -2px rgba(13, 148, 136, 0.52);
                }
            }

            .main-cat-icon {
                margin: 0 10px 0 0 !important;
                min-width: 28px !important;
                align-self: center;

                .folder-badge {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: #eaf7f4;
                    border: 1px solid rgba(16, 185, 129, 0.22);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 5px -1px rgba(13, 148, 136, 0.12);
                    transition: all 0.24s cubic-bezier(0.16, 1, 0.3, 1);

                    .v-icon {
                        color: #0d9488 !important;
                        transition: all 0.24s ease;
                    }

                    &.is-active {
                        background: linear-gradient(135deg, #10b981 0%, #0d9488 100%) !important;
                        border-color: rgba(255, 255, 255, 0.45) !important;
                        box-shadow: 0 4px 12px -2px rgba(13, 148, 136, 0.42);

                        .v-icon {
                            color: #ffffff !important;
                        }
                    }
                }
            }

            .main-cat-content {
                padding: 0 !important;
            }

            .v-list-group__header__append-icon {
                margin: 0 0 0 6px !important;
                min-width: 22px !important;
                .v-icon {
                    font-size: 20px !important;
                    color: #6b8e85 !important;
                }
            }
        }

        /* 展开态一级分类头部 */
        &.v-list-group--active > .v-list-group__header {
            background: linear-gradient(90deg, #e2f7f1 0%, #f0fbf8 100%) !important;
            border: 1px solid rgba(16, 185, 129, 0.22);
            color: #065f46 !important;

            .main-category-title {
                color: #065f46 !important;
                font-weight: 700;
            }

            .main-cat-icon .v-icon {
                color: #059669 !important;
            }

            .main-cat-count {
                background: rgba(16, 185, 129, 0.22);
                color: #047857;
            }

            .v-list-group__header__append-icon .v-icon {
                color: #059669 !important;
            }
        }
    }
}

.main-cat-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    line-height: 1.35 !important;
}

.main-category-title {
    font-size: 15.5px !important;
    font-weight: 600;
    color: #0f2922;
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    transition: color 0.18s ease;
}

/* 一级分类右侧子项数量徽章 */
.main-cat-count {
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    padding: 3px 8px;
    border-radius: 999px;
    background: #eaf3f0;
    color: #557970;
    flex-shrink: 0;
}

/* 二级分类树形缩进容器（带垂直树形引导线） */
::v-deep .v-list-group__items {
    position: relative;
    margin: 4px 0 8px 21px;
    padding: 2px 0 2px 10px;
    border-left: 2px solid #d5e9e3;

    .sub-cat-item {
        min-height: 36px !important;
        height: 36px;
        padding: 0 12px !important;
        margin: 3px 0 !important;
        border-radius: 8px !important;
        display: flex;
        align-items: center;
        transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;

        &::before {
            display: none !important;
        }

        .sub-cat-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #9fc2b9;
            margin-right: 11px;
            flex-shrink: 0;
            transition: transform 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease;
        }

        .sub-cat-content {
            padding: 0 !important;
        }

        .v-list-item__title {
            font-size: 14.5px !important;
            font-weight: 500;
            color: #2c4c43;
            line-height: 1.35 !important;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &:hover:not(.v-list-item--active) {
            background-color: #ecf9f5 !important;
            .sub-cat-dot {
                background: #10b981;
                transform: scale(1.15);
            }
            .v-list-item__title {
                color: #059669;
                font-weight: 600;
            }
        }

        /* 二级分类当前激活项 */
        &.v-list-item--active {
            background: linear-gradient(90deg, #10b981 0%, #0d9488 100%) !important;
            box-shadow: 0 4px 12px -2px rgba(13, 148, 136, 0.35);

            .sub-cat-dot {
                background: #ffffff;
                transform: scale(1.25);
                box-shadow: 0 0 0 2.5px rgba(255, 255, 255, 0.35);
            }

            .v-list-item__title {
                color: #ffffff !important;
                font-weight: 600;
            }
        }
    }
}

.category-quick-filter {
    padding: 12px 14px 6px;

    .filter-input-wrap {
        display: flex;
        align-items: center;
        height: 34px;
        padding: 0 10px;
        border-radius: 10px;
        background: #f4fbf9;
        border: 1px solid #cde6df;
        transition: all 0.2s ease;

        &:focus-within {
            background: #ffffff;
            border-color: #10b981;
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
        }

        .quick-filter-input {
            width: 100%;
            border: none;
            outline: none;
            background: transparent;
            font-size: 13px;
            color: #0f2922;
            font-weight: 500;

            &::placeholder {
                color: #84a39a;
                font-weight: 400;
            }
        }

        .clear-filter-btn {
            border: none;
            background: transparent;
            cursor: pointer;
            padding: 0;
            outline: none;
            display: flex;
            align-items: center;
        }
    }
}

</style>